import React, { useState, useEffect, useRef, useCallback } from "react";
import { productAPI } from "../api/apiService";

/* ─────────────────────────────────────────────────────────────────────────────
   SmartHSNInput
   Props:
     value         – current hsn_code string
     gstValue      – current gst number
     onHSNChange   – (hsn_code, gst_percentage) => void
     productName   – string (for saved-mapping lookup)
     mainCategory  – name of selected main category
     subCategory   – name of selected subcategory
   ───────────────────────────────────────────────────────────────────────────── */
export default function SmartHSNInput({
  value = "",
  gstValue = 0,
  onHSNChange,
  productName = "",
  mainCategory = "",
  subCategory = "",
}) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [matchStatus, setMatchStatus] = useState("none"); // "exact"|"similar"|"none"
  const [recentChips, setRecentChips] = useState([]);
  const [categoryHints, setCategoryHints] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalQuery, setModalQuery] = useState("");
  const [modalResults, setModalResults] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const modalInputRef = useRef(null);

  // ── Sync external value → internal query when parent resets the form ──────
  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // ── On mount: load recent chips & check saved mapping ─────────────────────
  useEffect(() => {
    loadRecentChips();
  }, []);

  // ── When product name changes, check for a saved mapping ──────────────────
  useEffect(() => {
    if (productName && productName.length > 2) {
      checkSavedMapping(productName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productName]);

  // ── When category changes, fetch category-based suggestions ───────────────
  useEffect(() => {
    if (mainCategory || subCategory) {
      fetchCategoryHints();
    } else {
      setCategoryHints([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCategory, subCategory]);

  // ── Close dropdown on outside click ──────────────────────────────────────
  useEffect(() => {
    function handleOutside(e) {
      if (!e.target || !document.contains(e.target)) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ── Focus modal search input when modal opens ─────────────────────────────
  useEffect(() => {
    if (showModal) {
      setTimeout(() => modalInputRef.current?.focus(), 150);
      runModalSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  // ─────────────────────────────────────────────────────────────────────────
  async function loadRecentChips() {
    try {
      const res = await productAPI.getHSNMapping();
      setRecentChips(res.data?.results || []);
    } catch (_) {}
  }

  async function checkSavedMapping(name) {
    try {
      const res = await productAPI.getHSNMapping({ product_name: name });
      if (res.data?.found) {
        const { hsn_code, gst_percentage } = res.data;
        setQuery(hsn_code);
        setMatchStatus("exact");
        onHSNChange?.(hsn_code, gst_percentage);
      }
    } catch (_) {}
  }

  async function fetchCategoryHints() {
    try {
      const res = await productAPI.suggestHSNByCategory({
        category: mainCategory,
        subcategory: subCategory,
        limit: 6,
      });
      setCategoryHints(res.data?.results || []);
    } catch (_) {}
  }

  // ── Debounced inline search ───────────────────────────────────────────────
  const handleInputChange = useCallback(
    (e) => {
      const q = e.target.value;
      setQuery(q);
      
      // Immediately notify parent of the new raw text so manual entries work
      onHSNChange?.(q, gstValue);

      if (!q.trim()) {
        setSuggestions([]);
        setShowDropdown(false);
        setMatchStatus("none");
        return;
      }

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const res = await productAPI.searchHSN({
            q,
            category: mainCategory,
            subcategory: subCategory,
            limit: 8,
          });
          const results = res.data?.results || [];
          setSuggestions(results);
          setShowDropdown(results.length > 0);

          // Determine match status
          const exact = results.find(
            (r) => r.hsn_code.toLowerCase() === q.toLowerCase()
          );
          if (exact) setMatchStatus("exact");
          else if (results.length > 0) setMatchStatus("similar");
          else setMatchStatus("none");
        } catch (_) {
          setSuggestions([]);
          setMatchStatus("none");
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [mainCategory, subCategory, onHSNChange]
  );

  // ── Select a suggestion from dropdown / modal ─────────────────────────────
  function selectHSN(hsn) {
    setQuery(hsn.hsn_code);
    setMatchStatus("exact");
    setShowDropdown(false);
    onHSNChange?.(hsn.hsn_code, hsn.gst_percentage);
    // Save mapping
    if (productName) {
      productAPI
        .saveHSNMapping({
          product_name: productName,
          hsn_code: hsn.hsn_code,
          gst_percentage: hsn.gst_percentage,
        })
        .then(() => loadRecentChips())
        .catch(() => {});
    }
  }

  // ── Modal search ──────────────────────────────────────────────────────────
  async function runModalSearch(q) {
    setModalLoading(true);
    try {
      const res = await productAPI.searchHSN({
        q,
        limit: 20,
      });
      setModalResults(res.data?.results || []);
    } catch (_) {
      setModalResults([]);
    } finally {
      setModalLoading(false);
    }
  }

  function handleModalQueryChange(e) {
    const q = e.target.value;
    setModalQuery(q);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runModalSearch(q), 300);
  }

  function handleModalSelect(hsn) {
    selectHSN(hsn);
    setShowModal(false);
    setModalQuery("");
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Match Status badge
  const matchBadge =
    matchStatus === "exact" ? (
      <span style={styles.badgeExact}>🟢 Exact Match Found</span>
    ) : matchStatus === "similar" ? (
      <span style={styles.badgeSimilar}>🟡 Similar Match</span>
    ) : query.length > 1 ? (
      <span style={styles.badgeNone}>🔴 HSN Not Found</span>
    ) : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {/* ── Input row ───────────────────────────────────────────── */}
      <div style={styles.inputRow}>
        <div style={styles.inputWrap}>
          <span style={styles.searchIcon}>🔎</span>
          <input
            id="smart-hsn-input"
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => query.length > 0 && suggestions.length > 0 && setShowDropdown(true)}
            placeholder="Search Product or HSN (e.g. Rice, Soap, 0904)"
            autoComplete="off"
            style={styles.input}
          />
          {loading && <span style={styles.spinner} className="hsn-spin">⟳</span>}
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setSuggestions([]);
                setMatchStatus("none");
                onHSNChange?.("", 0);
              }}
              style={styles.clearBtn}
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>

        {/* ── 🔍 Find button ──────────────────────────────────── */}
        <button
          type="button"
          onClick={() => setShowModal(true)}
          style={styles.findBtn}
          title="Browse full HSN master list"
        >
          🔍 Find
        </button>
      </div>

      {/* ── Match status badge ───────────────────────────────────── */}
      {matchBadge && <div style={styles.matchRow}>{matchBadge}</div>}

      {/* ── Dropdown suggestions ─────────────────────────────────── */}
      {showDropdown && suggestions.length > 0 && (
        <div style={styles.dropdown}>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={() => selectHSN(s)}
              style={styles.dropdownItem}
              className="hsn-dropdown-item"
            >
              <div style={styles.dropdownLeft}>
                <span style={styles.hsnCode}>{s.hsn_code}</span>
                <span style={styles.hsnDesc}>{s.description}</span>
              </div>
              <span style={styles.gstBadge}>GST {s.gst_percentage}%</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Category-based hints ─────────────────────────────────── */}
      {categoryHints.length > 0 && !showDropdown && !query && (
        <div style={styles.hintSection}>
          <p style={styles.hintLabel}>
            💡 Suggested for <strong>{subCategory || mainCategory}</strong>
          </p>
          <div style={styles.chipRow}>
            {categoryHints.map((h) => (
              <button
                key={h.id}
                type="button"
                onMouseDown={() => selectHSN(h)}
                style={styles.hintChip}
                title={`${h.description} – GST ${h.gst_percentage}%`}
              >
                <span style={styles.hintCode}>{h.hsn_code}</span>
                <span style={styles.hintDescShort}>{h.description.slice(0, 20)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent / Common chips ─────────────────────────────────── */}
      {recentChips.length > 0 && !query && (
        <div style={styles.recentSection}>
          <p style={styles.recentLabel}>🕐 Recent HSN</p>
          <div style={styles.chipRow}>
            {recentChips.slice(0, 6).map((chip, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={() => {
                  setQuery(chip.hsn_code);
                  setMatchStatus("exact");
                  onHSNChange?.(chip.hsn_code, chip.gst_percentage);
                }}
                style={styles.recentChip}
                title={`GST ${chip.gst_percentage}%`}
              >
                {chip.hsn_code}
                {chip.product_name && (
                  <span style={styles.recentName}>
                    {" "}
                    {chip.product_name.charAt(0).toUpperCase() +
                      chip.product_name.slice(1, 10)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══ HSN Master Modal ═══════════════════════════════════════ */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>📋 HSN Master List</h3>
                <p style={styles.modalSub}>
                  Search and select HSN code — GST auto-fills
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={styles.modalClose}
              >
                ✕
              </button>
            </div>

            {/* Modal search bar */}
            <div style={styles.modalSearchWrap}>
              <span style={styles.searchIcon}>🔎</span>
              <input
                ref={modalInputRef}
                type="text"
                value={modalQuery}
                onChange={handleModalQueryChange}
                placeholder="Search by product name, HSN code, or category…"
                style={styles.modalInput}
                autoComplete="off"
              />
              {modalLoading && (
                <span style={styles.spinner} className="hsn-spin">⟳</span>
              )}
            </div>

            {/* Results table */}
            <div style={styles.modalTableWrap}>
              {modalResults.length === 0 && !modalLoading && (
                <div style={styles.modalEmpty}>
                  {modalQuery
                    ? "No HSN codes found for your search."
                    : "Start typing to search…"}
                </div>
              )}
              {modalResults.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onMouseDown={() => handleModalSelect(r)}
                  style={styles.modalRow}
                  className="hsn-modal-row"
                >
                  <div style={styles.modalRowLeft}>
                    <span style={styles.modalHSNCode}>{r.hsn_code}</span>
                    <div>
                      <p style={styles.modalDesc}>{r.description}</p>
                      <p style={styles.modalCat}>
                        {r.category}
                        {r.subcategory && ` › ${r.subcategory}`}
                      </p>
                    </div>
                  </div>
                  <span style={styles.modalGST}>{r.gst_percentage}%</span>
                </button>
              ))}
            </div>

            <p style={styles.modalFooter}>
              Click any row to auto-fill HSN and GST
            </p>
          </div>
        </div>
      )}

      {/* Scoped keyframe + hover styles */}
      <style>{`
        .hsn-spin { display:inline-block; animation: hsn-rotate 1s linear infinite; }
        @keyframes hsn-rotate { to { transform: rotate(360deg); } }
        .hsn-dropdown-item:hover { background: #eff6ff !important; }
        .hsn-modal-row:hover { background: #eff6ff !important; border-color: #93c5fd !important; }
        @media (prefers-color-scheme: dark) {
          .hsn-dropdown-item:hover { background: #1e3a5f !important; }
          .hsn-modal-row:hover { background: #1e3a5f !important; border-color: #3b82f6 !important; }
        }
      `}</style>
    </div>
  );
}

/* ─── Inline styles (works without Tailwind or CSS modules) ──────────────── */
const styles = {
  inputRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  inputWrap: {
    position: "relative",
    flex: 1,
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    fontSize: "14px",
    pointerEvents: "none",
    zIndex: 1,
  },
  input: {
    width: "100%",
    paddingLeft: "36px",
    paddingRight: "36px",
    paddingTop: "9px",
    paddingBottom: "9px",
    borderRadius: "8px",
    border: "1.5px solid #bfdbfe",
    fontSize: "14px",
    outline: "none",
    background: "transparent",
    color: "inherit",
    transition: "border-color 0.15s",
    boxSizing: "border-box",
  },
  spinner: {
    position: "absolute",
    right: "36px",
    fontSize: "16px",
    color: "#3b82f6",
  },
  clearBtn: {
    position: "absolute",
    right: "10px",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "13px",
    color: "#9ca3af",
    padding: "2px 4px",
    lineHeight: 1,
  },
  findBtn: {
    whiteSpace: "nowrap",
    padding: "9px 14px",
    borderRadius: "8px",
    border: "1.5px solid #3b82f6",
    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    cursor: "pointer",
    flexShrink: 0,
    transition: "opacity 0.15s",
  },
  matchRow: {
    marginTop: "6px",
  },
  badgeExact: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#16a34a",
    background: "#dcfce7",
    borderRadius: "20px",
    padding: "2px 10px",
  },
  badgeSimilar: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#92400e",
    background: "#fef3c7",
    borderRadius: "20px",
    padding: "2px 10px",
  },
  badgeNone: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#dc2626",
    background: "#fee2e2",
    borderRadius: "20px",
    padding: "2px 10px",
  },
  /* Dropdown */
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    zIndex: 9999,
    background: "#fff",
    border: "1.5px solid #bfdbfe",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(59,130,246,0.15)",
    overflow: "hidden",
    maxHeight: "280px",
    overflowY: "auto",
  },
  dropdownItem: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    border: "none",
    borderBottom: "1px solid #f0f4ff",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    gap: "8px",
    transition: "background 0.12s",
  },
  dropdownLeft: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  hsnCode: {
    fontWeight: "800",
    fontSize: "13px",
    color: "#2563eb",
    fontFamily: "monospace",
    letterSpacing: "0.05em",
  },
  hsnDesc: {
    fontSize: "12px",
    color: "#374151",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "220px",
  },
  gstBadge: {
    whiteSpace: "nowrap",
    fontSize: "11px",
    fontWeight: "700",
    background: "#eff6ff",
    color: "#1d4ed8",
    borderRadius: "20px",
    padding: "2px 8px",
    border: "1px solid #bfdbfe",
    flexShrink: 0,
  },
  /* Hints from category */
  hintSection: {
    marginTop: "10px",
  },
  hintLabel: {
    fontSize: "11px",
    color: "#6366f1",
    fontWeight: "600",
    marginBottom: "6px",
    letterSpacing: "0.02em",
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  hintChip: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "4px 10px",
    borderRadius: "20px",
    border: "1.5px solid #c7d2fe",
    background: "#eef2ff",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "600",
    transition: "all 0.15s",
  },
  hintCode: {
    color: "#4338ca",
    fontFamily: "monospace",
  },
  hintDescShort: {
    color: "#6b7280",
    fontWeight: "400",
    fontSize: "11px",
  },
  /* Recent chips */
  recentSection: {
    marginTop: "10px",
  },
  recentLabel: {
    fontSize: "11px",
    color: "#9ca3af",
    fontWeight: "600",
    marginBottom: "6px",
    letterSpacing: "0.02em",
  },
  recentChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 10px",
    borderRadius: "20px",
    border: "1.5px solid #e5e7eb",
    background: "#f9fafb",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "700",
    color: "#374151",
    fontFamily: "monospace",
    transition: "all 0.15s",
  },
  recentName: {
    fontFamily: "inherit",
    fontWeight: "400",
    fontSize: "11px",
    color: "#6b7280",
  },
  /* Modal overlay */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    backdropFilter: "blur(2px)",
  },
  modalBox: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    width: "100%",
    maxWidth: "620px",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "20px 24px 16px",
    borderBottom: "1px solid #f0f4ff",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#1e40af",
    margin: 0,
  },
  modalSub: {
    fontSize: "12px",
    color: "#6b7280",
    margin: "4px 0 0",
  },
  modalClose: {
    background: "#f3f4f6",
    border: "none",
    borderRadius: "8px",
    width: "32px",
    height: "32px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "700",
    color: "#374151",
    flexShrink: 0,
  },
  modalSearchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    borderBottom: "1px solid #f0f4ff",
  },
  modalInput: {
    width: "100%",
    paddingLeft: "32px",
    paddingRight: "36px",
    paddingTop: "10px",
    paddingBottom: "10px",
    border: "1.5px solid #bfdbfe",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    background: "#f8faff",
    boxSizing: "border-box",
    color: "#111827",
  },
  modalTableWrap: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 0",
  },
  modalEmpty: {
    padding: "40px 24px",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: "14px",
  },
  modalRow: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 24px",
    border: "none",
    borderBottom: "1px solid #f9fafb",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.12s, border-color 0.12s",
    gap: "12px",
  },
  modalRowLeft: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    minWidth: 0,
  },
  modalHSNCode: {
    fontWeight: "900",
    fontSize: "15px",
    color: "#1d4ed8",
    fontFamily: "monospace",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
    background: "#eff6ff",
    padding: "3px 8px",
    borderRadius: "6px",
    border: "1px solid #bfdbfe",
    flexShrink: 0,
  },
  modalDesc: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#111827",
    margin: 0,
    marginBottom: "2px",
  },
  modalCat: {
    fontSize: "11px",
    color: "#6b7280",
    margin: 0,
  },
  modalGST: {
    whiteSpace: "nowrap",
    fontWeight: "800",
    fontSize: "13px",
    color: "#059669",
    background: "#ecfdf5",
    borderRadius: "20px",
    padding: "3px 10px",
    border: "1px solid #a7f3d0",
    flexShrink: 0,
  },
  modalFooter: {
    padding: "12px 24px",
    fontSize: "11px",
    color: "#9ca3af",
    borderTop: "1px solid #f0f4ff",
    textAlign: "center",
    margin: 0,
  },
};
